import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useChat } from '../contexts/ChatContext';
import { apiService } from '../services/api';
import LoginInterface from './LoginInterface';
import InterviewSetup from './InterviewSetup';
import CallInterface from './chat/CallInterface';
import TranscriptPanel from './TranscriptPanel';
import './InterviewApp.css';

const InterviewApp = () => {
  const { isAuthenticated, voiceConnected, logout, startInterviewWithSelection, currentInterviewId, accessToken, sessionId, setCurrentInterviewId } = useAuth();
  const { messages, TAB_TYPES, changeTab, loadConversationHistory } = useChat();
  const { t } = useTranslation();
  const [callEnded, setCallEnded] = useState(false);
  const [showTranscript, setShowTranscript] = useState(false);
  const [conversationLoaded, setConversationLoaded] = useState(false);

  // When authenticated, switch to call interface tab and show the call interface with transcript
  useEffect(() => {
    if (isAuthenticated && voiceConnected) {
      changeTab(TAB_TYPES.CALL_INTERFACE);
    }
  }, [isAuthenticated, voiceConnected, changeTab, TAB_TYPES.CALL_INTERFACE]);

  // Load conversation history when resuming interview
  useEffect(() => {
    const loadHistoricalConversation = async () => {
      if (!isAuthenticated || !currentInterviewId || !accessToken || !sessionId) {
        return;
      }

      if (conversationLoaded || messages.length > 0) {
        return; // Already loaded
      }

      try {
        console.log('🔄 Loading conversation history for resumed interview...');
        const interviewDetails = await apiService.getInterview(
          currentInterviewId,
          accessToken,
          sessionId
        );

        const interview = interviewDetails.data || interviewDetails;

        if (interview.conversation && interview.conversation.length > 0) {
          console.log('📥 Loading', interview.conversation.length, 'historical messages');
          loadConversationHistory(interview.conversation);
          setConversationLoaded(true);
        }
      } catch (error) {
        console.error('❌ Failed to load conversation history:', error);
      }
    };

    loadHistoricalConversation();
  }, [isAuthenticated, currentInterviewId, accessToken, sessionId, conversationLoaded, messages.length, loadConversationHistory]);

  // Show login if not authenticated or call has ended
  if (!isAuthenticated || callEnded) {
    return <LoginInterface onCallEnd={() => setCallEnded(false)} />;
  }

  // Show loading indicator if resuming interview (has interview ID but not connected yet)
  if (isAuthenticated && currentInterviewId && !voiceConnected) {
    const handleCancelResume = () => {
      console.log('🚫 User cancelled resume');
      setCurrentInterviewId(null);
      localStorage.removeItem('currentInterviewId');

      // Clear URL parameter
      const url = new URL(window.location.href);
      url.searchParams.delete('interviewId');
      window.history.replaceState({}, '', url.toString());
    };

    return (
      <div className="interview-app resuming-state">
        <div className="resuming-container">
          <div className="resuming-spinner"></div>
          <h2>Resuming Your Interview...</h2>
          <p>Please wait while we reconnect you to your interview session</p>
          <p className="session-id">Session: {currentInterviewId.substring(0, 8)}...</p>
          <button className="cancel-resume-btn" onClick={handleCancelResume}>
            Cancel & Return to Setup
          </button>
        </div>
      </div>
    );
  }

  // Show selection screen if authenticated but no interview in progress
  if (isAuthenticated && !currentInterviewId && !voiceConnected) {
    return <InterviewSetup onStartInterview={startInterviewWithSelection} />;
  }

  const handleEndCall = async () => {
    try {
      console.log('🔚 InterviewApp: Ending interview session...');

      // Note: Interview completion is already handled by POC backend when it receives
      // the end_interview_manual event from CallInterface. No need to call API here.

      // Step 1: Logout from authentication system
      await logout();
      console.log('🔓 InterviewApp: Logged out successfully');

      // Step 2: Set call ended state to trigger login interface
      setCallEnded(true);
      console.log('🏁 InterviewApp: Call ended, redirecting to login interface');

      // Step 3: Small delay to ensure state updates, then force redirect if needed
      setTimeout(() => {

        window.location.reload();

      }, 1000);

    } catch (error) {
      console.error('❌ InterviewApp: Error ending interview:', error);

      // Even if logout fails, still redirect to login
      setCallEnded(true);

      // Emergency redirect after a short delay
      setTimeout(() => {
        console.log('🚨 InterviewApp: Emergency redirect to login after error');
        window.location.href = '/';
      }, 500);
    }
  };

  // Get transcript from messages
  const getTranscript = () => {
    return messages
      .filter(msg => msg.type === 'user_speech_chunk' || msg.type === 'ai_speech_chunk')
      .map(msg => ({
        id: msg.id,
        speaker: msg.type === 'user_speech_chunk' ? t('chat.you') : t('chat.interviewer'),
        text: msg.content,
        timestamp: new Date(msg.timestamp)
      }));
  };

  return (
    <div className="interview-app">
      <div className="interview-header">
        <h2>{t('interview.title')}</h2>
        <div className="interview-actions">
          <button
            className="transcript-toggle-btn"
            onClick={() => setShowTranscript(!showTranscript)}
          >
            {showTranscript ? t('interview.hideTranscript') : t('interview.viewTranscript')}
          </button>
          <button
            className="end-interview-btn"
            onClick={handleEndCall}
          >
            {t('interview.leaveInterview')}
          </button>
          <button
            className="logout-btn"
            onClick={logout}
            title="Logout"
          >
            Logout
          </button>
        </div>
      </div>
      
      <div className="interview-content">
        <CallInterface onEndCall={handleEndCall} />
        
        {showTranscript && (
          <TranscriptPanel 
            transcript={getTranscript()} 
            onClose={() => setShowTranscript(false)}
          />
        )}
      </div>
    </div>
  );
};

export default InterviewApp;